import { useState } from "react";
import styled from "styled-components";
import { Box } from "rebass/styled-components";
import { FaCheck, FaRegCopy, FaRegQuestionCircle } from "react-icons/fa";

import { BIND, BIND_SECTION } from "../data";
import {
  ACCENT,
  Cmd,
  MetaLabel,
  Section,
  SectionHeading,
  SectionSubheading,
  Underlined,
} from "../../styledComponents";

const COPIED_MS = 1600;

export const Bind = () => {
  const [copied, setCopied] = useState(false);

  const copyBind = async () => {
    try {
      await navigator.clipboard.writeText(BIND);
    } catch (e) {
      /* clipboard unavailable */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_MS);
  };

  return (
    <Section py={[72, 96, 120]}>
      <MetaLabel>{BIND_SECTION.eyebrow}</MetaLabel>
      <SectionHeading mt={4}>
        {BIND_SECTION.headingLead}
        <Underlined>{BIND_SECTION.headingUnderlined}</Underlined>
      </SectionHeading>
      <SectionSubheading>{BIND_SECTION.subheading}</SectionSubheading>

      <Box mt={[7, 8, 8]} maxWidth={620} width="100%">
        <Snippet>
          <code>
            bind &quot;f3&quot; &quot;say <span>!allstar</span>&quot;
          </code>
          <CopyBtn
            type="button"
            $copied={copied}
            onClick={copyBind}
            aria-label={copied ? "Copied" : "Copy bind command"}
          >
            {copied ? <FaCheck /> : <FaRegCopy />}
          </CopyBtn>
        </Snippet>

        <Footnote>
          <Help tabIndex={0}>
            <FaRegQuestionCircle aria-hidden="true" />
            {BIND_SECTION.helpLabel}
            <Tooltip role="tooltip">
              <ol>
                {BIND_SECTION.helpSteps.map((step: string) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </Tooltip>
          </Help>
          <Dot aria-hidden="true">·</Dot>
          No bind? Just type <Cmd>!allstar</Cmd> in chat, it works the same.
        </Footnote>
      </Box>
    </Section>
  );
};

// Console-style snippet with a corner copy button rather than an input plus a
// side button, so it reads as a command you paste, not a form field.
const Snippet = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy900};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 14px;
  display: flex;
  padding: 16px 56px 16px 20px;
  position: relative;

  code {
    color: ${({ theme }) => theme.colors.ice};
    flex: 1;
    font-family: "SFMono-Regular", Menlo, Consolas, monospace;
    font-size: 15.5px;
    min-width: 0;
    overflow-x: auto;
    white-space: nowrap;
  }

  code span {
    color: ${ACCENT};
  }
`;

const CopyBtn = styled.button<{ $copied?: boolean }>`
  background: ${({ $copied }) =>
    $copied ? "transparent" : "rgba(255, 255, 255, 0.08)"};
  border: none;
  border-radius: 8px;
  color: ${({ $copied, theme }) => ($copied ? ACCENT : theme.colors.ice)};
  cursor: pointer;
  display: inline-grid;
  height: 34px;
  place-items: center;
  position: absolute;
  right: 11px;
  top: 50%;
  transform: translateY(-50%);
  transition: background 0.15s ease;
  width: 34px;

  &:hover {
    background: rgba(255, 255, 255, 0.16);
  }

  &:focus-visible {
    outline: 2px solid ${ACCENT};
    outline-offset: 2px;
  }

  svg {
    height: 15px;
    width: 15px;
  }
`;

const Footnote = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  margin-top: 16px;
`;

const Dot = styled.span`
  margin: 0 8px;
  opacity: 0.5;
`;

const Help = styled.span`
  align-items: center;
  color: ${ACCENT};
  cursor: help;
  display: inline-flex;
  font-weight: 600;
  gap: 6px;
  position: relative;

  svg {
    flex: none;
  }

  &:hover > [role="tooltip"],
  &:focus-within > [role="tooltip"] {
    opacity: 1;
    transform: translate(-50%, 0);
    visibility: visible;
  }
`;

const Tooltip = styled.div`
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  box-shadow: 0 24px 60px -24px rgba(0, 0, 0, 0.9);
  left: 50%;
  max-width: 86vw;
  opacity: 0;
  padding: 14px 16px 14px 6px;
  position: absolute;
  text-align: left;
  top: calc(100% + 10px);
  transform: translate(-50%, -4px);
  transition:
    opacity 0.15s ease,
    transform 0.15s ease,
    visibility 0.15s;
  visibility: hidden;
  width: 320px;
  z-index: 5;

  ol {
    display: flex;
    flex-direction: column;
    gap: 9px;
    margin: 0;
    padding-left: 24px;
  }

  li {
    color: ${({ theme }) => theme.colors.ice};
    font-size: 14.5px;
    font-weight: 400;
    line-height: 1.5;
  }

  &::after {
    background: ${({ theme }) => theme.colors.ned};
    border-left: 1px solid rgba(255, 255, 255, 0.12);
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    content: "";
    height: 9px;
    left: 50%;
    margin-left: -4px;
    position: absolute;
    top: -5px;
    transform: rotate(45deg);
    width: 9px;
  }
`;
